import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  LockKey,
  SignOut,
  EnvelopeSimple,
  Phone,
  ArrowClockwise,
  Trash,
  CheckCircle,
  WarningCircle,
  ArrowLeft,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { LOGO } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ja_admin_token";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.access_token);
    } catch (err) {
      setError(err.response?.data?.detail || "Anmeldung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1e2a6e] px-5 grain">
      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={LOGO} alt="JA! Consulting & Support" className="h-12 bg-white rounded-xl p-2" />
        </div>
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="rounded-3xl bg-white p-8 shadow-2xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="w-11 h-11 rounded-xl bg-[#1e2a6e] flex items-center justify-center">
              <LockKey size={22} weight="duotone" className="text-white" />
            </span>
            <div>
              <h1 className="font-heading text-xl font-bold text-[#0A0D1F]">Admin-Bereich</h1>
              <p className="text-sm text-[#5C6479]">Bitte anmelden</p>
            </div>
          </div>

          {error && (
            <div data-testid="admin-login-error" className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <label className="block text-sm font-medium text-[#0A0D1F] mb-1">E-Mail</label>
          <input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-gray-300 px-4 py-3 mb-4 outline-none focus:border-[#4a90d9] focus:ring-2 focus:ring-[#4a90d9]/30 transition"
            placeholder="info@jasupport.de"
            required
          />
          <label className="block text-sm font-medium text-[#0A0D1F] mb-1">Passwort</label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-gray-300 px-4 py-3 mb-6 outline-none focus:border-[#4a90d9] focus:ring-2 focus:ring-[#4a90d9]/30 transition"
            placeholder="••••••••"
            required
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-[#1e2a6e] px-6 py-3.5 font-semibold text-white hover:bg-[#4a90d9] transition-colors disabled:opacity-60"
          >
            {loading ? "Anmelden…" : "Anmelden"}
          </button>
        </form>
        <div className="text-center mt-6">
          <Link to="/" className="text-white/70 hover:text-white text-sm inline-flex items-center gap-2">
            <ArrowLeft size={16} weight="bold" /> Zurück zur Website
          </Link>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const authAxios = useCallback(
    () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await authAxios().get("/contact");
      setMessages(data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      setError("Laden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }, [authAxios, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Diese Anfrage wirklich löschen?")) return;
    try {
      await authAxios().delete(`/contact/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
    } catch {
      setError("Löschen fehlgeschlagen.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="JA!" className="h-9 w-auto" />
            <span className="font-heading font-bold text-[#0A0D1F] hidden sm:inline">Anfragen-Verwaltung</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-[#1e2a6e] hover:bg-gray-100 transition"
            >
              <ArrowClockwise size={16} weight="bold" /> Aktualisieren
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full bg-[#1e2a6e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#4a90d9] transition"
            >
              <SignOut size={16} weight="bold" /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-baseline justify-between mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0D1F]">
            Kontaktanfragen
          </h1>
          <span data-testid="admin-count" className="text-sm text-[#5C6479]">
            {messages.length} {messages.length === 1 ? "Anfrage" : "Anfragen"}
          </span>
        </div>

        {error && <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}

        {loading ? (
          <p className="text-[#5C6479]">Wird geladen…</p>
        ) : messages.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center text-[#5C6479]">
            Noch keine Anfragen vorhanden.
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-messages-list">
            {messages.map((m) => (
              <div
                key={m.id}
                data-testid="admin-message-card"
                className="rounded-2xl border border-gray-200 bg-white p-6 hover:border-[#4a90d9] transition-colors"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-lg font-bold text-[#0A0D1F]">{m.name}</h3>
                      {m.subject && (
                        <span className="rounded-full bg-[#4a90d9]/10 text-[#1e2a6e] text-xs font-semibold px-3 py-1">
                          {m.subject}
                        </span>
                      )}
                      {m.email_sent ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                          <CheckCircle size={14} weight="fill" /> E-Mail versendet
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium">
                          <WarningCircle size={14} weight="fill" /> nur gespeichert
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-[#5C6479]">
                      <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 hover:text-[#4a90d9]">
                        <EnvelopeSimple size={15} /> {m.email}
                      </a>
                      {m.phone && (
                        <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#4a90d9]">
                          <Phone size={15} /> {m.phone}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#8a90a0] whitespace-nowrap">{fmtDate(m.created_at)}</span>
                    <button
                      data-testid={`admin-delete-${m.id}`}
                      onClick={() => remove(m.id)}
                      className="text-[#8a90a0] hover:text-red-600 transition"
                      title="Löschen"
                    >
                      <Trash size={18} weight="bold" />
                    </button>
                  </div>
                </div>
                <p className="mt-4 text-[#0A0D1F] leading-relaxed whitespace-pre-wrap border-t border-gray-100 pt-4">
                  {m.message}
                </p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginView onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
